// HelpSection.jsx
import React, { useState, useContext, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Link,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import PlayCircleFilledWhiteIcon from "@mui/icons-material/PlayCircleFilledWhite";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import CloseIcon from "@mui/icons-material/Close";

import SwipeableViews from "react-swipeable-views";
import { virtualize } from "react-swipeable-views-utils";

import HelpPanel from "../GeneralComponents/HelpPanel";
import GeneralContext from "../GeneralContext/GeneralContext";
import PageButtonSimple from "../GeneralComponents/PageButtonSimple";
import PageButtonBlack from "../GeneralComponents/PageButtonBlack";
import PageButtonWhite from "../GeneralComponents/PageButtonWhite";

import {
  ROL_OP_ADMIN,
  ROL_OP_INS,
  ROL_ADMIN_SUBTEL,
  ROL_VAL_SUBTEL,
  ROL_ADMIN,
} from "../../Constants/Constants";

const VirtualizeSwipeableViews = virtualize(SwipeableViews);

const VIDEO_URL_DEFAULT =
  "https://storage.googleapis.com/bs-sagec-dev-www/tutoriales/operador_instalador/manual_operador_instalador.mp4";

const MOCK_VIDEO_THUMB_DEFAULT = "/assets/img-autenticacion.jpg";

const defaultCapsules = [
  {
    id: 1,
    title: "Actualización Operador (anular fas/fus)",
    thumb: MOCK_VIDEO_THUMB_DEFAULT,
    url: "https://storage.googleapis.com/bs-sagec-production-www/clips/01-Actualización-Operador-anular-fas-fus.mov",
  },
  {
    id: 2,
    title: "Operador Repaso FAS/FUS",
    thumb: MOCK_VIDEO_THUMB_DEFAULT,
    url: "https://storage.googleapis.com/bs-sagec-production-www/clips/02-Operador-Repaso-FAS-FUS-1.mov",
  },
  {
    id: 3,
    title: "Operador Actualizacion Inicio Home",
    thumb: MOCK_VIDEO_THUMB_DEFAULT,
    url: "https://storage.googleapis.com/bs-sagec-production-www/clips/03-Operador-Actualizacion-Inicio-Home-1.mov",
  },
  {
    id: 4,
    title: "Operador Actualizacion Reportes",
    thumb: MOCK_VIDEO_THUMB_DEFAULT,
    url: "https://storage.googleapis.com/bs-sagec-production-www/clips/04-Operador-Actualizacion-Reportes-1.mov",
  },
  {
    id: 5,
    title: "Operador Contingencia Programada",
    thumb: MOCK_VIDEO_THUMB_DEFAULT,
    url: "https://storage.googleapis.com/bs-sagec-production-www/clips/05-Operador-Contingencia-Programada-1.mov",
  },
  {
    id: 6,
    title: "Operador Contingencia Zonal",
    thumb: MOCK_VIDEO_THUMB_DEFAULT,
    url: "https://storage.googleapis.com/bs-sagec-production-www/clips/07-Operador-Contingencia-Zonal-1.mov",
  },
  {
    id: 7,
    title: "Operador Contingencia Especifica Externa",
    thumb: MOCK_VIDEO_THUMB_DEFAULT,
    url: "https://storage.googleapis.com/bs-sagec-production-www/clips/08-Operador-Contingencia-Especifica-Externa-1.mov",
  },
  {
    id: 8,
    title: "Operador Prefactura",
    thumb: MOCK_VIDEO_THUMB_DEFAULT,
    url: "https://storage.googleapis.com/bs-sagec-production-www/clips/09-Operador-Prefactura-1.mov",
  },
  {
    id: 9,
    title: "Operador Validaciones",
    thumb: MOCK_VIDEO_THUMB_DEFAULT,
    url: "https://storage.googleapis.com/bs-sagec-production-www/clips/10-Operador-Validaciones-1.mov",
  },
  {
    id: 10,
    title: "Operador Establecimientos",
    thumb: MOCK_VIDEO_THUMB_DEFAULT,
    url: "https://storage.googleapis.com/bs-sagec-production-www/clips/11-Operador-Establecimientos.mov",
  },
  {
    id: 11,
    title: "Operador Ayuda",
    thumb: MOCK_VIDEO_THUMB_DEFAULT,
    url: "https://storage.googleapis.com/bs-sagec-production-www/clips/12-Operador-Ayuda.mov",
  },
];

const MANUAL_LINKS_BY_ROLE = {
  [ROL_OP_INS]: {
    url: "https://dummy.sagec.cl/manual-operador-instalador.pdf",
    label: "Manual Operador Instalador",
  },
  [ROL_OP_ADMIN]: {
    url: "https://dummy.sagec.cl/manual-operador-admin.pdf",
    label: "Manual Operador Admin",
  },
  [ROL_ADMIN_SUBTEL]: {
    url: "https://dummy.sagec.cl/manual-subtel-admin.pdf",
    label: "Manual Administrador SUBTEL",
  },
  [ROL_VAL_SUBTEL]: {
    url: "https://dummy.sagec.cl/manual-subtel-validador.pdf",
    label: "Manual Validador SUBTEL",
  },
  [ROL_ADMIN]: {
    url: "https://dummy.sagec.cl/manual-admin-general.pdf",
    label: "Manual Administrador General",
  },
};

const HelpSection = ({
  mainVideo = {
    title: "Video capacitación completa",
    url: VIDEO_URL_DEFAULT,
    thumb: MOCK_VIDEO_THUMB_DEFAULT,
  },
  capsules = defaultCapsules,
}) => {
  const { setPageTitle, includeRol } = useContext(GeneralContext);
  const [slideIndex, setSlideIndex] = useState(0);

  const [videoModal, setVideoModal] = useState({
    open: false,
    title: "",
    url: "",
  });

  useEffect(() => {
    setPageTitle("Contacto de Soporte");
  }, [setPageTitle]);

  const isOpInstalador = includeRol([ROL_OP_INS]);
  const isOpAdmin = includeRol([ROL_OP_ADMIN]);
  const isSubtel = includeRol([ROL_ADMIN_SUBTEL, ROL_VAL_SUBTEL]);
  const isAdmin = includeRol([ROL_ADMIN]);

  const showOperatorHelp = isOpInstalador || isOpAdmin || isSubtel || isAdmin;

  const manualUrl = useMemo(() => {
    if (isAdmin && MANUAL_LINKS_BY_ROLE[ROL_ADMIN]) {
      return MANUAL_LINKS_BY_ROLE[ROL_ADMIN].url;
    }
    if (isOpInstalador && MANUAL_LINKS_BY_ROLE[ROL_OP_INS]) {
      return MANUAL_LINKS_BY_ROLE[ROL_OP_INS].url;
    }
    if (isOpAdmin && MANUAL_LINKS_BY_ROLE[ROL_OP_ADMIN]) {
      return MANUAL_LINKS_BY_ROLE[ROL_OP_ADMIN].url;
    }
    if (includeRol([ROL_ADMIN_SUBTEL]) && MANUAL_LINKS_BY_ROLE[ROL_ADMIN_SUBTEL]) {
      return MANUAL_LINKS_BY_ROLE[ROL_ADMIN_SUBTEL].url;
    }
    if (includeRol([ROL_VAL_SUBTEL]) && MANUAL_LINKS_BY_ROLE[ROL_VAL_SUBTEL]) {
      return MANUAL_LINKS_BY_ROLE[ROL_VAL_SUBTEL].url;
    }
    return "#";
  }, [isAdmin, isOpInstalador, isOpAdmin, includeRol]);

  const openUrl = (url) => {
    if (!url || url === "#") return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleOpenVideo = ({ title, url }) => {
    setVideoModal({
      open: true,
      title,
      url,
    });
  };

  const handleCloseVideo = () => {
    setVideoModal((prev) => ({
      ...prev,
      open: false,
      url: "",
    }));
  };

  const effectiveCapsules = useMemo(() => {
    if (!capsules || !capsules.length) return [];
    if (!isOpInstalador) return capsules;

    return capsules.filter(
      (c) => !c.title?.toLowerCase().includes("prefactura")
    );
  }, [capsules, isOpInstalador]);

  const slideRenderer = ({ index, key }) => {
    const n = effectiveCapsules.length;

    if (n === 0) {
      return (
        <Box key={key} sx={{ p: 2, height: "100%" }}>
          <Typography align="center">
            No hay cápsulas disponibles.
          </Typography>
        </Box>
      );
    }

    const start = ((index % n) + n) % n;

    const visibleCapsules = [
      effectiveCapsules[start],
      effectiveCapsules[(start + 1) % n],
      effectiveCapsules[(start + 2) % n],
    ];

    return (
      <Box key={key} sx={{ px: 1, height: "100%" }}>
        <Grid container spacing={2} sx={{ height: "100%" }}>
          {visibleCapsules.map((capsule) => (
            <Grid
              key={capsule.id}
              size={{ xs: 12, sm: 4 }}
              sx={{ height: "100%", display: "flex" }}
            >
              <Box
                sx={{
                  borderRadius: 2,
                  overflow: "hidden",
                  cursor: "pointer",
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                }}
                onClick={() =>
                  handleOpenVideo({ title: capsule.title, url: capsule.url })
                }
              >
                <Box
                  component="img"
                  src={capsule.thumb}
                  alt={capsule.title}
                  sx={{
                    width: "100%",
                    aspectRatio: "16 / 9",
                    display: "block",
                    objectFit: "cover",
                    flexShrink: 0,
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    top: "40%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    pointerEvents: "none",
                  }}
                >
                  <PlayCircleFilledWhiteIcon
                    sx={{ fontSize: 64, color: "#0F69C4" }}
                  />
                </Box>

                <Box
                  sx={{
                    p: 1.5,
                    flexGrow: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    align="center"
                  >
                    {capsule.title}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  const handleNext = () => {
    setSlideIndex((prev) => prev + 1);
  };

  const handleBack = () => {
    setSlideIndex((prev) => prev - 1);
  };

  if (!showOperatorHelp) {
    return null;
  }

  return (
    <>
      <Box sx={{ p: 0, mb: 3 }}>
        <Grid container spacing={3} sx={{ alignItems: "stretch" }}>
          <Grid size={{ xs: 12, md: 6 }} sx={{ display: "flex" }}>
            <HelpPanel title="Manuales de Usuario">
              <Box sx={{ p: 4, height: "100%" }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <SchoolOutlinedIcon
                    sx={{ mr: 1.5, fontSize: 32, color: "#2196F3" }}
                  />
                  <Typography variant="h5" fontWeight={700}>
                    Manual Rol Operador
                  </Typography>
                </Box>

                <Typography variant="body1" sx={{ pb: 4, display: "block" }}>
                  Puede descargar el último{" "}
                  <strong>manual actualizado el 10-11-2025</strong> en{" "}
                  <Link
                    component="button"
                    type="button"
                    sx={{ fontWeight: 600, pb: "3px" }}
                    onClick={() => openUrl(manualUrl)}
                  >
                    este enlace
                  </Link>{" "}
                  y aprender a utilizar nuestra plataforma en detalle.
                </Typography>

                <PageButtonSimple onClick={() => openUrl(manualUrl)} pad="8px 16px">
                  Descargar Manual en PDF
                </PageButtonSimple>
              </Box>
            </HelpPanel>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }} sx={{ display: "flex" }}>
            <HelpPanel title={mainVideo.title || "Video capacitación completa"}>
              <Box
                sx={{
                  p: 2,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    borderRadius: 3,
                    overflow: "hidden",
                    maxWidth: 420,
                    width: "100%",
                    bgcolor: "black",
                    cursor: "pointer",
                  }}
                  onClick={() =>
                    handleOpenVideo({
                      title: mainVideo.title,
                      url: mainVideo.url,
                    })
                  }
                >
                  <Box
                    component="img"
                    src={mainVideo.thumb || MOCK_VIDEO_THUMB_DEFAULT}
                    alt={mainVideo.title}
                    sx={{
                      width: "100%",
                      aspectRatio: "16 / 9",
                      display: "block",
                      objectFit: "cover",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      pointerEvents: "none",
                    }}
                  >
                    <PlayCircleFilledWhiteIcon
                      sx={{ fontSize: 80, color: "#0F69C4" }}
                    />
                  </Box>
                </Box>
              </Box>
            </HelpPanel>
          </Grid>

          <Grid size={12}>
  <HelpPanel title="Cápsulas de capacitación">
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "56px 1fr 56px", // espacio reservado a ambos lados
          alignItems: "stretch",
          minHeight: { xs: 230, md: 230 },
        }}
      >
        {/* Columna izquierda: botón anterior */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <IconButton
            size="small"
            onClick={handleBack}
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
            }}
          >
            <KeyboardArrowLeft />
          </IconButton>
        </Box>

        {/* Columna central: carrusel */}
        <Box
          sx={{
            width: "100%",
            maxWidth: "100%",
            overflow: "hidden",
          }}
        >
          <VirtualizeSwipeableViews
            index={slideIndex}
            onChangeIndex={setSlideIndex}
            enableMouseEvents
            slideRenderer={slideRenderer}
            style={{ width: "100%" }}
          />
        </Box>

        {/* Columna derecha: botón siguiente */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <IconButton
            size="small"
            onClick={handleNext}
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
            }}
          >
            <KeyboardArrowRight />
          </IconButton>
        </Box>
      </Box>
    </Box>
  </HelpPanel>
</Grid>

        </Grid>
      </Box>

      <Dialog
        open={videoModal.open}
        onClose={handleCloseVideo}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pr: 5, textAlign: "center" }}>
          {videoModal.title}
          <IconButton
            aria-label="close"
            onClick={handleCloseVideo}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {videoModal.url && (
            <>
              <Box
                component="video"
                src={videoModal.url}
                controls
                autoPlay
                sx={{
                  width: "100%",
                  borderRadius: 1,
                  outline: "none",
                }}
              />
              <Grid
                container
                spacing={2}
                justifyContent="center"
                sx={{ mt: 3, mb: 1 }}
              >
                <Grid>
                  <PageButtonWhite width="200px" onClick={handleCloseVideo}>
                    Cerrar
                  </PageButtonWhite>
                </Grid>
                <Grid>
                  <PageButtonBlack
                    width="200px"
                    onClick={() => openUrl(videoModal.url)}
                  >
                    Descargar
                  </PageButtonBlack>
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default HelpSection;
